import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { Modal } from '../../../components/Modal';
import type { FeedbackCampaign, FeedbackResponse, RatingConfig } from '../../../types/dynamic-feedback';
import { feedbackCampaignsService } from '../services/feedbackCampaignsService';

interface CampaignResponsesModalProps {
  campaign: FeedbackCampaign;
  onClose: () => void;
}

export const CampaignResponsesModal = ({ campaign, onClose }: CampaignResponsesModalProps) => {
  const [responses, setResponses] = useState<FeedbackResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadResponses = async () => {
      try {
        setLoading(true);
        const fetchedResponses = await feedbackCampaignsService.getResponses(campaign.id);
        setResponses(fetchedResponses);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load responses');
      } finally {
        setLoading(false);
      }
    };
    loadResponses();
  }, [campaign.id]);



  const calculateAverageRatings = () => {
    const ratings: Record<string, number[]> = {};

    responses.forEach((response) => {
      response.answers.forEach((answer) => {
        if (answer.questionType === 'rating' && typeof answer.answer === 'number') {
          if (!ratings[answer.questionId]) {
            ratings[answer.questionId] = [];
          }
          ratings[answer.questionId].push(answer.answer);
        }
      });
    });

    const averages: Record<string, number> = {};
    Object.entries(ratings).forEach(([questionId, values]) => {
      averages[questionId] = values.reduce((a, b) => a + b, 0) / values.length;
    });

    return averages;
  };

  const exportToCSV = () => {
    if (responses.length === 0) return;

    // Build CSV header
    const headers = ['User ID', 'User Email', 'Location', 'Submitted At'];
    campaign.questions.forEach((q) => {
      headers.push(`Q${q.order + 1}: ${q.question}`);
    });
    headers.push('Metadata');

    // Build CSV rows
    const rows = responses.map((response) => {
      const row = [
        response.userId,
        response.userEmail || '',
        response.location,
        response.submittedAt.toISOString(),
      ];

      campaign.questions.forEach((question) => {
        const answer = response.answers.find((a) => a.questionId === question.id);
        if (answer) {
          if (Array.isArray(answer.answer)) {
            row.push(answer.answer.join('; '));
          } else {
            row.push(String(answer.answer));
          }
        } else {
          row.push('');
        }
      });

      // Add metadata as JSON string
      row.push(response.metadata ? JSON.stringify(response.metadata) : '');

      return row;
    });

    // Create CSV content
    const csvContent = [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${campaign.name.replace(/[^a-z0-9]/gi, '_')}_responses.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const averageRatings = calculateAverageRatings();

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Responses: ${campaign.name}`}
      size="xl"
    >
      {loading ? (
        <div className="py-8">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-900">{responses.length}</div>
              <div className="text-sm text-blue-700">Total Responses</div>
            </div>

            {campaign.questions
              .filter((q) => q.type === 'rating')
              .map((question) => {
                const avgRating = question.id ? averageRatings[question.id] : undefined;
                return (
                  <div key={question.id} className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-900">
                      {avgRating?.toFixed(1) || 'N/A'}
                    </div>
                    <div className="text-sm text-green-700 truncate" title={question.question}>
                      Avg: {question.question}
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Export Button */}
          {responses.length > 0 && (
            <div className="flex justify-end">
              <Button onClick={exportToCSV} variant="outline" className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          )}

          {/* Responses Table */}
          {responses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No responses yet for this campaign
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Submitted
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Metadata
                      </th>
                      {campaign.questions.map((q, i) => (
                        <th
                          key={q.id}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                        >
                          Q{i + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {responses.map((response) => (
                      <tr key={response.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="truncate max-w-[150px]" title={response.userEmail || ''}>
                            {response.userEmail || response.userId}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                            {response.location}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {response.submittedAt.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {response.metadata && Object.keys(response.metadata).length > 0 ? (
                            <div className="max-w-[200px]">
                              <details className="cursor-pointer">
                                <summary className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                                  View JSON ({Object.keys(response.metadata).length} fields)
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(response.metadata, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No metadata</span>
                          )}
                        </td>
                        {campaign.questions.map((question) => {
                          const answer = response.answers.find((a) => a.questionId === question.id);
                          return (
                            <td key={question.id} className="px-4 py-3 text-sm">
                              {answer ? (
                                Array.isArray(answer.answer) ? (
                                  <div className="truncate max-w-[150px]" title={answer.answer.join(', ')}>
                                    {answer.answer.join(', ')}
                                  </div>
                                ) : question.type === 'rating' ? (
                                  <span className="font-semibold">
                                    {answer.answer}/{(question.config as RatingConfig)?.max || 10}
                                  </span>
                                ) : (
                                  <div className="truncate max-w-[150px]" title={String(answer.answer)}>
                                    {answer.answer}
                                  </div>
                                )
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
